import {
  PIXEL_PERSON_AUTO_HAIR_COLORS,
  PIXEL_PERSON_AUTO_SKIN_PRESET_KEYS,
  PIXEL_PERSON_BEANIE_COLORS,
  PIXEL_PERSON_CAP_COLORS,
  PIXEL_PERSON_HAIR_COLORS,
  PIXEL_PERSON_HEADWEAR_PALETTES,
  PIXEL_PERSON_PANTS_COLORS,
  PIXEL_PERSON_SHIRT_COLORS,
  PIXEL_PERSON_SHOE_COLORS,
  PIXEL_PERSON_SKIN_TONE_PRESETS,
  SYSTEM_AGENT_HEADWEAR_ID,
  SYSTEM_AGENT_PIXEL_COLORS,
  MASTER_AGENT_HEADWEAR_ID,
  MASTER_AGENT_PIXEL_COLORS,
} from './pixelPersonPalettes';
import { PIXEL_PERSON_HAIR_STYLE_IDS } from './pixelPersonHairStyles';
import {
  PIXEL_PERSON_HEADWEAR_IDS,
  PIXEL_PERSON_RANDOM_HEADWEAR_IDS,
} from './pixelPersonHeadwear';

export {
  SYSTEM_AGENT_HEADWEAR_ID,
  SYSTEM_AGENT_PIXEL_COLORS,
  MASTER_AGENT_HEADWEAR_ID,
  MASTER_AGENT_PIXEL_COLORS,
};

const CSS_COLOR_PATTERN =
  /^(#[0-9a-fA-F]{3,8}|rgb\(\s*[\d.]+\s*,\s*[\d.]+\s*,\s*[\d.]+\s*\)|hsl\(\s*[\d.]+\s*,\s*[\d.]+%?\s*,\s*[\d.]+%?\s*\))$/;

function parseHex(hex) {
  const normalized = hex.replace('#', '');
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
}

function toHex(r, g, b) {
  const clamp = (n) => Math.max(0, Math.min(255, Math.round(n)));
  return `#${[r, g, b]
    .map((channel) => clamp(channel).toString(16).padStart(2, '0'))
    .join('')}`;
}

/** Lighten (amount > 0) or darken (amount < 0) a hex color toward white/black. */
export function shadeColor(hex, amount) {
  const { r, g, b } = parseHex(hex);
  if (amount < 0) {
    const factor = 1 + amount;
    return toHex(r * factor, g * factor, b * factor);
  }
  return toHex(
    r + (255 - r) * amount,
    g + (255 - g) * amount,
    b + (255 - b) * amount
  );
}

export function hashCode(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

function mulberry32(seed) {
  let state = seed;
  return () => {
    state |= 0;
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function pick(arr, rng) {
  return arr[Math.floor(rng() * arr.length)];
}

/** Allow only simple CSS color literals for SVG fill props. */
export function sanitizeCssColor(value, fallback) {
  if (typeof value !== 'string') {
    return fallback;
  }
  const trimmed = value.trim();
  if (!trimmed || !CSS_COLOR_PATTERN.test(trimmed)) {
    return fallback;
  }
  return trimmed;
}

function resolveSkinTone(tone, rng) {
  if (tone === undefined) {
    const presetKey =
      PIXEL_PERSON_AUTO_SKIN_PRESET_KEYS[
        Math.floor(rng() * PIXEL_PERSON_AUTO_SKIN_PRESET_KEYS.length)
      ];
    return PIXEL_PERSON_SKIN_TONE_PRESETS[presetKey];
  }
  if (typeof tone === 'number') {
    return PIXEL_PERSON_SKIN_TONE_PRESETS[tone] ?? PIXEL_PERSON_SKIN_TONE_PRESETS[3];
  }
  const num = parseInt(tone, 10);
  if (num >= 1 && num <= 5 && String(num) === tone) {
    return PIXEL_PERSON_SKIN_TONE_PRESETS[num];
  }
  return sanitizeCssColor(tone, PIXEL_PERSON_SKIN_TONE_PRESETS[3]);
}

function resolveHeadwearColors(headwearId, rng) {
  if (!headwearId || headwearId === 'none') {
    return { headwear: null, headwearAccent: null };
  }

  const fixed = PIXEL_PERSON_HEADWEAR_PALETTES[headwearId];
  if (fixed) {
    return fixed;
  }

  if (headwearId === 'cap') {
    const headwear = pick(PIXEL_PERSON_CAP_COLORS, rng);
    return { headwear, headwearAccent: shadeColor(headwear, -0.22) };
  }

  if (headwearId === 'beanie') {
    const headwear = pick(PIXEL_PERSON_BEANIE_COLORS, rng);
    return { headwear, headwearAccent: shadeColor(headwear, -0.18) };
  }

  return { headwear: null, headwearAccent: null };
}

export function resolvePixelPersonColors(props, seed, headwearId = 'none') {
  const rng = mulberry32(seed);
  const autoHairColors = PIXEL_PERSON_AUTO_HAIR_COLORS;
  const shirt = props.shirt
    ? sanitizeCssColor(props.shirt, pick(PIXEL_PERSON_SHIRT_COLORS, rng))
    : pick(PIXEL_PERSON_SHIRT_COLORS, rng);

  const headwearColors = resolveHeadwearColors(headwearId, rng);

  return {
    hair: props.hair
      ? sanitizeCssColor(props.hair, pick(autoHairColors, rng))
      : pick(autoHairColors, rng),
    skin: resolveSkinTone(props.tone, rng),
    shirt,
    pants: props.pants
      ? sanitizeCssColor(props.pants, pick(PIXEL_PERSON_PANTS_COLORS, rng))
      : pick(PIXEL_PERSON_PANTS_COLORS, rng),
    shoes: props.shoes
      ? sanitizeCssColor(props.shoes, pick(PIXEL_PERSON_SHOE_COLORS, rng))
      : pick(PIXEL_PERSON_SHOE_COLORS, rng),
    outline: shadeColor(shirt, -0.72),
    eye: shadeColor(shirt, -0.85),
    ...headwearColors,
  };
}

export function resolvePixelPersonSeed({ name, seed, index }) {
  if (typeof seed === 'number') {
    return seed;
  }
  if (name) {
    return hashCode(String(name));
  }
  if (typeof index === 'number') {
    return hashCode(`pixelperson-${index}`);
  }
  return hashCode('pixelperson-default');
}

/**
 * Deterministic hairstyle for a sprite seed.
 * @param {number} seed
 * @param {string | undefined} styleOverride
 * @returns {string}
 */
export function resolvePixelPersonHairStyleId(seed, styleOverride) {
  if (styleOverride && PIXEL_PERSON_HAIR_STYLE_IDS.includes(styleOverride)) {
    return styleOverride;
  }
  const index = hashCode(`${seed}-hair-style`) % PIXEL_PERSON_HAIR_STYLE_IDS.length;
  return PIXEL_PERSON_HAIR_STYLE_IDS[index];
}

/**
 * Deterministic field-services headwear for a sprite seed.
 * @param {number} seed
 * @param {string | undefined} styleOverride
 * @returns {string}
 */
export function resolvePixelPersonHeadwearId(seed, styleOverride) {
  if (styleOverride && PIXEL_PERSON_HEADWEAR_IDS.includes(styleOverride)) {
    return styleOverride;
  }

  const roll = hashCode(`${seed}-headwear`) % 100;
  if (roll < 35) {
    return 'none';
  }

  const index = hashCode(`${seed}-headwear-style`) % PIXEL_PERSON_RANDOM_HEADWEAR_IDS.length;
  return PIXEL_PERSON_RANDOM_HEADWEAR_IDS[index];
}
